import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Download, HelpCircle, Loader2 } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { instagramProfileUrlFromHandle } from "@/lib/instagramProfileUrl";
import {
  applyReportDateToSearchParams,
  formatYmdLocal,
  parseReportDateSearchParams,
  type ReportDatePreset,
} from "@/lib/reportDateFilterUrl";
import type { DateRange as DayPickerRange } from "react-day-picker";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type EngagedFan = {
  _id: string;
  igUserId?: string;
  igUsername?: string;
  numEngagements: number;
  lastActive?: string;
  joinedAt?: string;
};

type FanListSegment = "new" | "active" | "inactive";

type EngagedFansStats = {
  count: number;
  activeCount: number;
  inactiveCount: number;
  isAllTime: boolean;
};

type DailyRow = {
  date: string;
  contacts: number;
  active: number;
};

const INSTAGRAM_FANS_HELP_SEEN_KEY = "instagram-fans-list-help-seen";
const STRATEGY_CALL_URL = "https://cal.com/kaizenstrategy/15min";

function InstagramFansHelpModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Understanding Your Audience</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-foreground">
          <p>See who actually engages with your posts</p>
          <p>Active fans matter far more than follower count</p>
          <p>These are the fans most likely to buy from you</p>
        </div>
        <DialogFooter className="flex-col items-center gap-1 text-center sm:flex-col">
          <p className="text-xs text-foreground">Engram (formerly Kaizen Place)</p>
          <p className="text-xs text-foreground">
            Questions?{" "}
            <a
              href="mailto:hello@engram.sh"
              className="font-medium text-primary underline underline-offset-4"
            >
              Contact Us
            </a>
          </p>
        </DialogFooter>
        {/* Hidden for now; re-enable to drive strategy call bookings.
        <DialogFooter className="sm:justify-center">
          <Button asChild>
            <a href={STRATEGY_CALL_URL} target="_blank" rel="noopener noreferrer">
              Book a Free Strategy Call
            </a>
          </Button>
        </DialogFooter>
        */}
      </DialogContent>
    </Dialog>
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function getDateRangeForPeriod(period: ReportDatePreset): { start: Date | null; end: Date | null } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  switch (period) {
    case "1W": {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { start, end };
    }
    case "1M": {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      return { start, end };
    }
    case "MTD": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      return { start, end };
    }
    case "YTD": {
      const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      return { start, end };
    }
    case "All":
      return { start: null, end: null };
  }
}

function buildFilledDailyRows({
  start,
  end,
  sparse,
}: {
  start: Date;
  end: Date;
  sparse: DailyRow[];
}) {
  const map = new Map(sparse.map((r) => [r.date, r]));
  const rows: { key: string; label: string; contacts: number; active: number }[] = [];
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  while (d <= endDay) {
    const key = formatYmdLocal(d);
    const existing = map.get(key);
    rows.push({
      key,
      label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      contacts: existing?.contacts ?? 0,
      active: existing?.active ?? 0,
    });
    d.setDate(d.getDate() + 1);
  }
  return rows;
}

function CardDailyChart({
  dataKey,
  data,
  formatValue,
  colorVar,
}: {
  dataKey: "contacts" | "active";
  data: { key: string; label: string; contacts: number; active: number }[];
  formatValue: (value: number) => string;
  colorVar: string;
}) {
  return (
    <div className="h-24 w-full mt-3 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis hide domain={[0, "auto"]} />
          <Tooltip
            formatter={(value: number | undefined) => [formatValue(value ?? 0), undefined]}
            labelFormatter={(_, items) => {
              const row = items?.[0]?.payload as { label?: string } | undefined;
              return row?.label ?? "";
            }}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={colorVar}
            fill={colorVar}
            fillOpacity={0.15}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function InstagramFansListRoute() {
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { preset, customRange } = useMemo(
    () =>
      parseReportDateSearchParams(new URLSearchParams(location.search), {
        defaultPreset: "1M",
      }),
    [location.search],
  );
  const segmentParam = new URLSearchParams(location.search).get("segment");
  const segment: FanListSegment =
    segmentParam === "active" ? "active"
    : segmentParam === "inactive" ? "inactive"
    : "new";
  const [tempDateRange, setTempDateRange] = useState<DayPickerRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [fans, setFans] = useState<EngagedFan[]>([]);
  const [stats, setStats] = useState<EngagedFansStats | null>(null);
  const [daily, setDaily] = useState<DailyRow[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);

  useEffect(() => {
    if (localStorage.getItem(INSTAGRAM_FANS_HELP_SEEN_KEY) !== "1") {
      setHelpOpen(true);
    }
  }, []);

  const handleHelpOpenChange = (open: boolean) => {
    setHelpOpen(open);
    if (!open) {
      localStorage.setItem(INSTAGRAM_FANS_HELP_SEEN_KEY, "1");
    }
  };

  const chartRows = useMemo(() => {
    if (!daily) return null;
    const { start, end } =
      customRange?.from && customRange?.to
        ? { start: customRange.from, end: customRange.to }
        : getDateRangeForPeriod(preset);
    if (!start || !end) return null;
    return buildFilledDailyRows({ start, end, sparse: daily });
  }, [daily, preset, customRange]);

  useEffect(() => {
    async function fetchFans() {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (debouncedSearch.trim()) {
          params.set("search", debouncedSearch.trim());
        }
        if (segment !== "new") {
          params.set("segment", segment);
        }
        applyReportDateToSearchParams(params, { preset, customRange });
        const query = params.toString();
        const response = await fetch(
          `/api/instagram/engaged-fans/list${query ? `?${query}` : ""}`,
        );
        if (response.ok) {
          const data = (await response.json()) as {
            fans: EngagedFan[];
            stats: EngagedFansStats;
            daily?: DailyRow[];
          };
          setFans(data.fans ?? []);
          setStats(data.stats ?? null);
          setDaily(data.daily);
        }
      } catch (error) {
        console.error("Failed to fetch engaged fans:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFans();
  }, [debouncedSearch, preset, customRange, segment]);

  const periods: ReportDatePreset[] = ["1W", "1M", "MTD", "YTD", "All"];
  const isAllTime = stats?.isAllTime ?? (preset === "All" && !customRange);

  const formatPeriodDateRange = (period: ReportDatePreset): string => {
    if (period === "All") return "All";
    const { start, end } = getDateRangeForPeriod(period);
    if (start && end) {
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return period;
  };

  const handlePeriodClick = (period: ReportDatePreset) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        applyReportDateToSearchParams(next, { preset: period, customRange: undefined });
        return next;
      },
      { replace: true },
    );
  };

  const handleDateRangeSelect = (range: DayPickerRange | undefined) => {
    setTempDateRange(range);
  };

  const handleDone = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          applyReportDateToSearchParams(next, { preset: "All", customRange: tempDateRange });
          return next;
        },
        { replace: true },
      );
    }
    setCalendarOpen(false);
  };

  const handleCancel = () => {
    setTempDateRange(customRange);
    setCalendarOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setCalendarOpen(open);
    if (open) {
      setTempDateRange(customRange);
    }
  };

  const formatDateRange = () => {
    if (customRange?.from && customRange?.to) {
      return `${customRange.from.toLocaleDateString()} - ${customRange.to.toLocaleDateString()}`;
    }
    return "Custom Range";
  };

  const handleSegmentClick = (next: FanListSegment) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === "new") {
          nextParams.delete("segment");
        } else {
          nextParams.set("segment", next);
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  const exportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) {
        params.set("search", debouncedSearch.trim());
      }
      if (segment !== "new") {
        params.set("segment", segment);
      }
      applyReportDateToSearchParams(params, { preset, customRange });
      const query = params.toString();
      const response = await fetch(
        `/api/instagram/engaged-fans/export${query ? `?${query}` : ""}`,
      );
      if (!response.ok) {
        console.error("Failed to export engaged fans CSV");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `instagram-fans-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export engaged fans CSV:", error);
    } finally {
      setIsExporting(false);
    }
  }, [debouncedSearch, preset, customRange, segment]);

  return (
    <div className="container mx-auto p-4 max-w-5xl">
      <InstagramFansHelpModal open={helpOpen} onOpenChange={handleHelpOpenChange} />
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {periods.map((period) => (
          <Button
            key={period}
            variant={preset === period && !customRange ? "default" : "outline"}
            size="sm"
            onClick={() => handlePeriodClick(period)}
          >
            {period}
          </Button>
        ))}
        <Popover open={calendarOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant={customRange || (preset !== "All" && !customRange) ? "default" : "outline"}
              size="sm"
              className="gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {customRange?.from && customRange?.to
                ? formatDateRange()
                : preset !== "All" && !customRange
                  ? formatPeriodDateRange(preset)
                  : "Custom Range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={tempDateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={1}
              className="rounded-md border-0"
            />
            <div className="flex justify-end gap-2 p-3 border-t">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleDone}
                disabled={!tempDateRange?.from || !tempDateRange?.to}
              >
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 ml-auto"
          aria-label="Help"
          onClick={() => setHelpOpen(true)}
        >
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card
          className={`cursor-pointer transition-colors ${
            segment === "new" ? "ring-2 ring-primary" : "hover:bg-accent"
          }`}
          onClick={() => handleSegmentClick("new")}
        >
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">
                {(stats?.count ?? 0).toLocaleString()}
              </p>
              <p className="text-muted-foreground mt-2">
                {isAllTime ? "Contacts" : "New Contacts"}
              </p>
            </div>
            {chartRows && (
              <CardDailyChart
                dataKey="contacts"
                data={chartRows}
                colorVar="var(--chart-3)"
                formatValue={(v) => Math.round(v).toLocaleString()}
              />
            )}
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-colors ${
            segment === "active" ? "ring-2 ring-primary" : "hover:bg-accent"
          }`}
          onClick={() => handleSegmentClick("active")}
        >
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">
                {(stats?.activeCount ?? 0).toLocaleString()}
              </p>
              <p className="text-muted-foreground mt-2">Active Contacts</p>
            </div>
            {chartRows && (
              <CardDailyChart
                dataKey="active"
                data={chartRows}
                colorVar="var(--chart-2)"
                formatValue={(v) => Math.round(v).toLocaleString()}
              />
            )}
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-colors ${
            segment === "inactive" ? "ring-2 ring-primary" : "hover:bg-accent"
          }`}
          onClick={() => handleSegmentClick("inactive")}
        >
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">
                {(stats?.inactiveCount ?? 0).toLocaleString()}
              </p>
              <p className="text-muted-foreground mt-2">Inactive Contacts</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4 flex gap-2">
        <Input
          placeholder="Search by username…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          disabled={fans.length === 0 || isExporting}
          onClick={() => void exportCsv()}
          className="hidden md:inline-flex shrink-0"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead className="text-right">Comments</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fans.length > 0 ? (
              fans.map((fan) => {
                const profileUrl = fan.igUsername
                  ? instagramProfileUrlFromHandle(fan.igUsername)
                  : null;
                return (
                  <TableRow key={fan._id}>
                    <TableCell>
                      {fan.igUsername ? (
                        profileUrl ? (
                          <a
                            href={profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            @{fan.igUsername.replace(/^@+/, "")}
                          </a>
                        ) : (
                          `@${fan.igUsername.replace(/^@+/, "")}`
                        )
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right">{fan.numEngagements}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="h-24 text-center text-muted-foreground">
                  {debouncedSearch.trim() ?
                    "No fans match your search."
                  : segment === "active" ?
                    "No active contacts found."
                  : segment === "inactive" ?
                    "No inactive contacts found."
                  : "No engaged fans found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
